import { useState } from 'react';
import { Link, useSearchParams } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { BILLING_METHOD_LABELS, BILLING_ORDER_STATUS_LABELS, BILLING_PAYMENT_STATUS_LABELS, formatCents, grossCents, PLAN_LABELS, PLAN_PRICES_GROSS_MONTH_CENTS, type BillingOverview, type BillingPaymentStatus } from '@kontor/shared';
import { api, ApiError } from '../api';
import './billing.css';

const dateFormat = new Intl.DateTimeFormat('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
const day = (iso: string) => dateFormat.format(new Date(`${iso}T12:00:00`));
const tone = (s: BillingPaymentStatus) => (s === 'paid' ? 'badge--won' : s === 'open' ? 'badge--in_progress' : s === 'cancelled' || s === 'refunded' ? '' : 'badge--rejected');

/**
 * Abschnitte „Ihr Paket“ und „Zahlungen und Rechnungen“ (KON-180, KON-182) auf der Seite Wirkia Konto: Bestellung, Zahlungen mit Rechnungen zum Herunterladen und Kündigung zum Ende
 * der bezahlten Zeit. Wer noch im Test ist, kommt von hier zur Bestellung. Bis zum 21.10.2026 war das eine eigene Seite „Abonnement und Rechnungen“.
 */
export function BillingSections() {
  const client = useQueryClient();
  const [params] = useSearchParams();
  const [message, setMessage] = useState<{ ok: boolean; text: string } | null>(null);
  const overview = useQuery({ queryKey: ['billing'], queryFn: async () => (await api<{ data: BillingOverview }>('GET', '/billing')).data });
  const cancel = useMutation({
    mutationFn: () => api('POST', '/billing/cancel'),
    onSuccess: () => {
      client.invalidateQueries({ queryKey: ['billing'] });
      setMessage({ ok: true, text: 'Ihre Kündigung ist eingegangen. Eine Bestätigung kommt per E-Mail.' });
    },
    onError: (e) => setMessage({ ok: false, text: e instanceof ApiError ? e.message : 'Die Kündigung hat nicht geklappt.' }),
  });

  if (overview.isLoading) return <p className="muted">Wird geladen …</p>;
  const data = overview.data;
  if (!data) return <div className="alert alert--error" role="alert">Die Abrechnung konnte nicht geladen werden.</div>;
  const order = data.order;

  return (
    <>
      {params.get('bestellt') && <div className="alert alert--ok" role="status">Vielen Dank für Ihre Bestellung. Die Grenzen des Tests entfallen, und alle Ihre Daten bleiben erhalten. Rechnung und Bestätigung kommen per E-Mail.</div>}
      {message && <div className={`alert ${message.ok ? 'alert--ok' : 'alert--error'}`} role={message.ok ? 'status' : 'alert'}>{message.text}</div>}

      {!order && (
        <div className="card">
          <h2>Noch kein Paket</h2>
          <p>{data.canOrder ? 'Sie testen Wirkia gerade. Bestellen Sie ein Paket, dann entfallen die Grenzen des Tests, und alle Ihre Daten bleiben erhalten.' : 'Für Ihren Zugang gibt es keine Bestellung bei Wirkia.'}</p>
          {data.canOrder && <p><Link to="/bestellen" className="btn btn--primary">Paket bestellen</Link></p>}
        </div>
      )}

      {order && (
        <div className="card">
          <h2>Ihr Paket</h2>
          <dl className="billing-facts">
            <dt>Paket</dt><dd>{PLAN_LABELS[order.plan]}, {formatCents(PLAN_PRICES_GROSS_MONTH_CENTS[order.plan])} brutto je Benutzer und Monat, abgerechnet für zwölf Monate im Voraus ({formatCents(grossCents(order.netCents))})</dd>
            <dt>Zahlungsart</dt><dd>{BILLING_METHOD_LABELS[order.paymentMethod]}</dd>
            <dt>Status</dt><dd><span className="badge">{BILLING_ORDER_STATUS_LABELS[order.status]}</span></dd>
            {order.periodEnd && <><dt>Abgerechnet bis</dt><dd>{day(order.periodEnd)}</dd></>}
            {order.cancelAt && <><dt>Gekündigt zum</dt><dd>{day(order.cancelAt)}</dd></>}
            {order.downgrade && <><dt>Wechsel vorgemerkt</dt><dd>Auf {PLAN_LABELS[order.downgrade.plan]} ab dem {day(order.downgrade.effectiveOn)}, {formatCents(PLAN_PRICES_GROSS_MONTH_CENTS[order.downgrade.plan])} brutto je Benutzer und Monat. Zurücknehmen können Sie es oben bei den Paketen.</dd></>}
            <dt>Rechnungsanschrift</dt><dd>{order.billing.company}, {order.billing.street}, {order.billing.postalCode} {order.billing.city}</dd>
            <dt>Bestellt</dt><dd>{dateFormat.format(new Date(order.acceptedAt))} mit {order.termsVersion} und {order.avvVersion}</dd>
          </dl>
          {(order.status === 'active' || order.status === 'pending') && (
            <div className="form-actions">
              <button
                className="btn btn--secondary"
                type="button"
                disabled={cancel.isPending}
                onClick={() => {
                  if (window.confirm(order.status === 'pending' ? 'Die Bestellung abbrechen?' : `Wirklich zum Ende der bezahlten Zeit kündigen${order.periodEnd ? ` (${day(order.periodEnd)})` : ''}?\n\nBis dahin läuft Ihr Zugang weiter. Laden Sie vorher Ihre Daten über „Import und Export“ herunter.`)) cancel.mutate();
                }}
              >
                {cancel.isPending ? 'Wird gekündigt …' : order.status === 'pending' ? 'Bestellung abbrechen' : 'Kündigen'}
              </button>
            </div>
          )}
        </div>
      )}

      {data.payments.length > 0 && (
        <>
          <h2>Zahlungen und Rechnungen</h2>
          <div className="table-wrap">
            <table className="table table--cards">
              <thead>
                <tr><th>Zeitraum</th><th>Leistung</th><th>Betrag</th><th>Status</th><th>Fällig</th><th>Rechnung</th></tr>
              </thead>
              <tbody>
                {data.payments.map((p) => (
                  <tr key={p.id}>
                    <td data-label="Zeitraum">{day(p.periodStart)} bis {day(p.periodEnd)}</td>
                    <td data-label="Leistung">{p.kind === 'addons' ? 'Zusatzbuchungen' : 'Paket'}</td>
                    <td data-label="Betrag">{formatCents(p.grossCents)} <span className="muted small">brutto</span></td>
                    <td data-label="Status"><span className={`badge ${tone(p.status)}`}>{BILLING_PAYMENT_STATUS_LABELS[p.status]}</span>{p.paidAt && <div className="muted small">am {dateFormat.format(new Date(p.paidAt))}</div>}</td>
                    <td data-label="Fällig">{p.dueOn ? day(p.dueOn) : <span className="muted">–</span>}</td>
                    <td data-label="Rechnung">{p.hasInvoice ? <a href={`/api/v1/billing/payments/${p.id}/invoice`} download>{p.invoiceNumber ?? 'Rechnung'} (PDF)</a> : <span className="muted">folgt</span>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </>
  );
}
